import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Fetches all user submissions from Firebase
 * @returns {Promise<Array>} Array of user submission objects
 */
export async function getAllUserSubmissions() {
  try {
    const q = query(collection(db, 'user_submissions'), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const submissions = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Helper function to convert Firebase Timestamp to readable date
      const convertTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        if (timestamp?.toDate) {
          return timestamp.toDate().toLocaleString();
        }
        if (timestamp?.seconds) {
          // Handle Firestore Timestamp object
          return new Date(timestamp.seconds * 1000).toLocaleString();
        }
        return timestamp;
      };
      
      // Convert all fields, handling timestamps specially
      const convertedData = {};
      Object.keys(data).forEach(key => {
        const value = data[key];
        // Check if it's a timestamp field or Timestamp object
        if (value?.toDate || value?.seconds || key.includes('_start') || key.includes('_end') || key === 'createdAt' || key === 'lastUpdated' || key.includes('storeVisit') || key.includes('codeCopied')) {
          convertedData[key] = convertTimestamp(value);
        } else {
          convertedData[key] = value;
        }
      });
      
      submissions.push({
        id: doc.id,
        ...convertedData
      });
    });
    
    return submissions;
  } catch (error) {
    console.error('Error fetching submissions:', error);
    
    // Provide specific error messages for common issues
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. Please update Firebase rules to allow reads for admin.');
    } else if (error.code === 'failed-precondition') {
      throw new Error('Firestore database not properly configured.');
    } else {
      throw new Error(`Firebase error: ${error.message}`);
    }
  }
}

/**
 * Converts submissions data to CSV format
 * @param {Array} submissions - Array of submission objects
 * @returns {string} CSV string
 */
export function convertToCSV(submissions) {
  if (submissions.length === 0) return '';
  
  // Collect all unique keys from all submissions to create comprehensive headers
  const allKeys = new Set();
  submissions.forEach(submission => {
    Object.keys(submission).forEach(key => allKeys.add(key));
  });
  
  // Convert to array and sort for consistent column order
  // Prioritize important fields first
  const priorityFields = ['id', 'createdAt', 'name', 'email', 'answer', 'session_score', 'session_misses'];
  const otherFields = Array.from(allKeys)
    .filter(key => !priorityFields.includes(key))
    .sort();
  
  const headers = [...priorityFields.filter(key => allKeys.has(key)), ...otherFields];
  
  // Helper function to safely convert value to CSV cell
  const toCsvCell = (value) => {
    if (value === null || value === undefined) return 'N/A';
    
    // Handle Firebase Timestamp objects that weren't converted
    if (value?.toDate) {
      return `"${value.toDate().toLocaleString()}"`;
    }
    if (value?.seconds) {
      return `"${new Date(value.seconds * 1000).toLocaleString()}"`;
    }
    
    // Handle Date objects
    if (value instanceof Date) {
      return `"${value.toLocaleString()}"`;
    }
    
    // Handle objects (convert to JSON string)
    if (typeof value === 'object') {
      return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
    }
    
    // Handle strings - escape quotes and wrap in quotes if contains comma or newline
    if (typeof value === 'string') {
      if (value.includes(',') || value.includes('\n') || value.includes('"')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }
    
    // Handle numbers, booleans, etc.
    return String(value);
  };
  
  // Convert data rows
  const rows = submissions.map(submission => 
    headers.map(header => toCsvCell(submission[header]))
  );
  
  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map(row => row.join(','))
    .join('\n');
    
  return csvContent;
}

/**
 * Downloads submissions as CSV file
 */
export async function downloadSubmissionsAsCSV() {
  try {
    const submissions = await getAllUserSubmissions();
    const csvContent = convertToCSV(submissions);
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `user_submissions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`Downloaded ${submissions.length} submissions as CSV`);
    return submissions.length;
  } catch (error) {
    console.error('Error downloading CSV:', error);
    throw error;
  }
}